/**
 * Outputs of the Constellation entity-event nodes: the
 * "<microservice> - On <Entity> create|update|delete" triggers and their
 * "- Wait" counterparts.
 *
 * Since #6070 the event creator is exposed as a `creator` PassportReference
 * ({ userName, passportId }) instead of the raw `creatorId` string. The engine
 * (Flow.Engine KafkaEventManager) resolves it from the event's `creatorId`.
 *
 * Kept free of editor imports so it can be unit-tested on its own.
 */

/**
 * @typedef {object} TEventOutput
 * @property {string} key the output key, also the key read by the engine
 * @property {unknown} socket the rete socket typing the output
 * @property {boolean} decompose whether the output gets the "+" (Decompose) button
 * @property {string} [label] display name, defaults to the key
 * @property {boolean} [deprecated] kept for diagrams that still wire it
 */

export const EVENT_TYPES = {
  update: 'handleUpdateEntity',
  create: 'handleCreateEntity',
  delete: 'handleDeleteEntity',
}

export const CREATOR_OUTPUT_KEY = 'creator'

/**
 * Output deprecated by #6070. It keeps working, in the editor and in the
 * engine, but only appears on the nodes of diagrams that already wire it: a
 * node built today never offers it, so no new diagram can depend on it.
 */
export const LEGACY_CREATOR_OUTPUT_KEY = 'creatorId'

export const PASSPORT_REFERENCE_ENTITY = 'PassportReference'

/**
 * The `creator` output: a decomposable PassportReference when the swagger
 * definitions expose that entity, a plain object otherwise (no "+" button
 * then: the Decompose node needs a named entity to list its fields).
 *
 * @param {Record<string, unknown>} sockets the editor sockets
 * @returns {TEventOutput}
 */
export const getCreatorOutput = sockets => {
  const referenceSocket = sockets[`${PASSPORT_REFERENCE_ENTITY}Socket`]

  if (referenceSocket === undefined) {
    console.warn(
      `Flow: no "${PASSPORT_REFERENCE_ENTITY}" entity in the swagger definitions, ` +
        `the "${CREATOR_OUTPUT_KEY}" trigger output falls back to a plain object.`,
    )

    return {
      key: CREATOR_OUTPUT_KEY,
      socket: sockets.objectSocket,
      decompose: false,
    }
  }

  return { key: CREATOR_OUTPUT_KEY, socket: referenceSocket, decompose: true }
}

/**
 * The deprecated `creatorId` output: the raw passport id, as a string, exactly
 * as it behaved before #6070. Only added to nodes that already wire it.
 *
 * @param {Record<string, unknown>} sockets the editor sockets
 * @returns {TEventOutput}
 */
export const getDeprecatedCreatorIdOutput = sockets => ({
  key: LEGACY_CREATOR_OUTPUT_KEY,
  label: `${LEGACY_CREATOR_OUTPUT_KEY} (deprecated)`,
  socket: sockets.stringSocket,
  decompose: false,
  deprecated: true,
})

/**
 * Ordered outputs of an event node.
 *
 * @param {string} event one of EVENT_TYPES
 * @param {string} entity the swagger entity name, e.g. "Alarm"
 * @param {Record<string, unknown>} sockets the editor sockets
 * @param {{ deprecatedCreatorId?: boolean }} [options] include the deprecated
 *   `creatorId` output, in the position it occupied before #6070
 * @returns {TEventOutput[]}
 */
export const getEventOutputs = (
  event,
  entity,
  sockets,
  { deprecatedCreatorId = false } = {},
) => {
  const outputs = getCurrentEventOutputs(event, entity, sockets)

  if (!deprecatedCreatorId || outputs.length === 0) return outputs

  const [creator, ...rest] = outputs

  return [creator, getDeprecatedCreatorIdOutput(sockets), ...rest]
}

const getCurrentEventOutputs = (event, entity, sockets) => {
  const creator = getCreatorOutput(sockets)
  const traceId = {
    key: 'traceId',
    socket: sockets.stringSocket,
    decompose: false,
  }
  const entityOutput = {
    key: 'entity',
    socket: sockets[`${entity}Socket`],
    decompose: true,
  }
  const id = { key: 'id', socket: sockets.stringSocket, decompose: false }

  switch (event) {
    case EVENT_TYPES.update:
      return [
        creator,
        traceId,
        entityOutput,
        { key: 'entityId', socket: sockets.stringSocket, decompose: false },
        id,
      ]
    case EVENT_TYPES.create:
      return [creator, traceId, entityOutput, id]
    case EVENT_TYPES.delete:
      return [
        creator,
        traceId,
        entityOutput,
        id,
        { key: 'reason', socket: sockets.stringSocket, decompose: false },
      ]
    default:
      return []
  }
}
